import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { getSettings, updateSettings, getIntegrations, getCalendarStatus, getCalendarLoginUrl, disconnectCalendar, getTodoistLists } from "../api";
import { Slider } from "../components/ui/slider";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { SlidersHorizontal, Plug, Sun, Moon, Monitor, CalendarDays, Pill, Plus, X, ListChecks } from "lucide-react";
import { useTheme } from "next-themes";
import { CycleHistory } from "../components/CycleHistory";

const THEME_OPTIONS = [
  { id: "light", label: "Light", icon: Sun },
  { id: "dark", label: "Dark", icon: Moon },
  { id: "system", label: "System", icon: Monitor },
];

const WEIGHT_KEYS = [
  { key: "cycle", label: "Cycle phase" },
  { key: "sleep", label: "Sleep" },
  { key: "mood", label: "Mood" },
  { key: "motivation", label: "Motivation" },
];

const DAYS = ["M", "T", "W", "T", "F", "S", "S"];

export const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [integrations, setIntegrations] = useState({ todoist: false, ultrahuman: false });
  const [cal, setCal] = useState({ configured: false, connected: false, can_write: false });
  const [saving, setSaving] = useState(false);
  const [newSupp, setNewSupp] = useState("");
  const [lists, setLists] = useState({ projects: [], labels: [] });
  const { theme, setTheme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(() => {
    getSettings().then(setSettings);
    getIntegrations().then(setIntegrations);
    getCalendarStatus().then(setCal);
    getTodoistLists().then(setLists).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const c = searchParams.get("calendar");
    if (c === "connected") {
      toast.success("Google Calendar connected 🌿");
      getCalendarStatus().then(setCal);
      setSearchParams({}, { replace: true });
    } else if (c === "error") {
      toast.error("Couldn't connect Google Calendar — please try again");
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const connectCal = async () => {
    try {
      const { authorization_url } = await getCalendarLoginUrl();
      window.location.href = authorization_url;
    } catch {
      toast.error("Google Calendar isn't configured");
    }
  };

  const disconnectCal = async () => {
    await disconnectCalendar();
    setCal((c) => ({ ...c, connected: false, can_write: false }));
    toast.success("Google Calendar disconnected");
  };

  const addSupplement = () => {
    const name = newSupp.trim();
    if (!name) return;
    setSettings((s) => ({ ...s, supplements: [...s.supplements, { name, days: [] }] }));
    setNewSupp("");
  };

  const removeSupplement = (idx) =>
    setSettings((s) => ({ ...s, supplements: s.supplements.filter((_, i) => i !== idx) }));

  const toggleSuppDay = (idx, day) =>
    setSettings((s) => ({
      ...s,
      supplements: s.supplements.map((sup, i) => {
        if (i !== idx) return sup;
        const days = sup.days || [];
        return { ...sup, days: days.includes(day) ? days.filter((d) => d !== day) : [...days, day].sort() };
      }),
    }));

  const onSelectEssentials = async (val) => {
    const [type, id] = val.split(":");
    const pool = type === "project" ? lists.projects : lists.labels;
    const found = pool.find((x) => String(x.id) === id);
    const de = { type, id, name: found?.name || id };
    setSettings((s) => ({ ...s, daily_essentials: de }));
    try {
      const updated = await updateSettings({ daily_essentials: de });
      setSettings(updated);
      toast.success(`Daily essentials set to "${de.name}"`);
    } catch {
      toast.error("Couldn't save your selection");
    }
  };

  if (!settings) return <p className="text-stone-400">Loading…</p>;

  const total = Object.values(settings.weights).reduce((a, b) => a + Number(b), 0);

  const setWeight = (key, val) =>
    setSettings((s) => ({ ...s, weights: { ...s.weights, [key]: val / 100 } }));

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateSettings({
        weights: settings.weights,
        avg_cycle_length: Number(settings.avg_cycle_length),
        period_length: Number(settings.period_length),
        heavy_projects: settings.heavy_projects,
        supplements: settings.supplements,
      });
      setSettings(updated);
      toast.success("Settings saved");
    } catch {
      toast.error("Could not save settings");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <h2 className="font-heading text-3xl font-bold text-[#3b2a24] dark:text-stone-100">Tune your planner</h2>

      {/* Appearance */}
      <div className="rounded-3xl bg-[#faf4ee] dark:bg-stone-900 border border-stone-100 dark:border-stone-800 p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="appearance-card">
        <div className="flex items-center gap-2 mb-4">
          <Moon className="h-5 w-5 text-stone-400" />
          <h3 className="font-heading text-xl font-bold text-[#3b2a24] dark:text-stone-100">Appearance</h3>
        </div>
        <div className="grid grid-cols-3 gap-2 bg-stone-100 dark:bg-stone-800 rounded-2xl p-1.5">
          {THEME_OPTIONS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`theme-${id}`}
              onClick={() => setTheme(id)}
              className={`flex flex-col items-center gap-1.5 py-3 rounded-xl text-xs font-semibold transition-colors ${
                theme === id
                  ? "bg-white dark:bg-stone-700 shadow-sm text-[#3b2a24] dark:text-stone-100 dark:text-stone-100"
                  : "text-stone-500 dark:text-stone-400"
              }`}
            >
              <Icon className="h-5 w-5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Weights */}
      <div className="rounded-3xl bg-[#faf4ee] dark:bg-stone-900 border border-stone-100 dark:border-stone-800 p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="flex items-center gap-2 mb-1">
          <SlidersHorizontal className="h-5 w-5 text-stone-400" />
          <h3 className="font-heading text-xl font-bold text-[#3b2a24] dark:text-stone-100">Capacity weights</h3>
        </div>
        <p className={`text-xs mb-6 ${Math.abs(total - 1) > 0.011 ? "text-rose-500" : "text-stone-400"}`}>
          Total: {Math.round(total * 100)}% {Math.abs(total - 1) > 0.011 ? "(aim for 100%)" : ""}
        </p>
        <div className="space-y-6">
          {WEIGHT_KEYS.map(({ key, label }) => (
            <div key={key}>
              <Label className="text-sm text-stone-600 dark:text-stone-300 flex justify-between mb-2">
                <span>{label}</span>
                <span className="font-semibold text-[#3b2a24] dark:text-stone-100">{Math.round(settings.weights[key] * 100)}%</span>
              </Label>
              <Slider
                data-testid={`weight-${key}`}
                value={[Math.round(settings.weights[key] * 100)]}
                onValueChange={(v) => setWeight(key, v[0])}
                max={100}
                step={5}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Cycle config */}
      <div className="rounded-3xl bg-[#faf4ee] dark:bg-stone-900 border border-stone-100 dark:border-stone-800 p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-4">
        <h3 className="font-heading text-xl font-bold text-[#3b2a24] dark:text-stone-100">Your cycle</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-stone-600 dark:text-stone-300">Avg cycle length (days)</Label>
            <Input
              data-testid="avg-cycle-length"
              type="number"
              value={settings.avg_cycle_length}
              onChange={(e) => setSettings((s) => ({ ...s, avg_cycle_length: e.target.value }))}
              className="mt-2 h-12 rounded-2xl border-stone-200 dark:border-stone-700"
            />
          </div>
          <div>
            <Label className="text-sm text-stone-600 dark:text-stone-300">Period length (days)</Label>
            <Input
              data-testid="period-length"
              type="number"
              value={settings.period_length}
              onChange={(e) => setSettings((s) => ({ ...s, period_length: e.target.value }))}
              className="mt-2 h-12 rounded-2xl border-stone-200 dark:border-stone-700"
            />
          </div>
        </div>
        <p className="text-xs text-stone-400">Your cycle isn't textbook — that's the whole point. These tune the phase estimate.</p>
      </div>

      <CycleHistory />

      {/* Lists */}
      <div className="rounded-3xl bg-[#faf4ee] dark:bg-stone-900 border border-stone-100 dark:border-stone-800 p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-4">
        <h3 className="font-heading text-xl font-bold text-[#3b2a24] dark:text-stone-100">Lists</h3>
        <div>
          <Label className="text-sm text-stone-600 dark:text-stone-300">Heavy (high-effort) Todoist projects</Label>
          <Input
            data-testid="heavy-projects"
            value={settings.heavy_projects.join(", ")}
            onChange={(e) => setSettings((s) => ({ ...s, heavy_projects: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) }))}
            className="mt-2 h-12 rounded-2xl border-stone-200 dark:border-stone-700"
            placeholder="Work, Admin, Finance"
          />
        </div>
      </div>

      {/* Daily essentials */}
      <div className="rounded-3xl bg-[#faf4ee] dark:bg-stone-900 border border-stone-100 dark:border-stone-800 p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-3" data-testid="daily-essentials-card">
        <div className="flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-stone-400" />
          <h3 className="font-heading text-xl font-bold text-[#3b2a24] dark:text-stone-100">Daily essentials</h3>
        </div>
        <p className="text-xs text-stone-500 dark:text-stone-400">
          The Todoist list you'll see even on low-capacity days. Tracked by ID, so renaming it in Todoist won't break it.
        </p>
        <Select value={settings.daily_essentials ? `${settings.daily_essentials.type}:${settings.daily_essentials.id}` : undefined} onValueChange={onSelectEssentials}>
          <SelectTrigger data-testid="essentials-select" className="h-12 rounded-2xl border-stone-200 dark:border-stone-700">
            <SelectValue placeholder="Choose a Todoist list…">{settings.daily_essentials?.name}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {lists.projects.length > 0 && (
              <SelectGroup>
                <SelectLabel>Projects</SelectLabel>
                {lists.projects.map((p) => (
                  <SelectItem key={`project:${p.id}`} value={`project:${p.id}`} data-testid={`essentials-option-project-${p.id}`}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
            {lists.labels.length > 0 && (
              <SelectGroup>
                <SelectLabel>Labels</SelectLabel>
                {lists.labels.map((l) => (
                  <SelectItem key={`label:${l.id}`} value={`label:${l.id}`} data-testid={`essentials-option-label-${l.id}`}>
                    @{l.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
        {settings.daily_essentials ? (
          <p className="text-xs text-stone-500 dark:text-stone-400" data-testid="essentials-current">
            Currently showing: <span className="font-semibold text-[#3b2a24] dark:text-stone-200">{settings.daily_essentials.name}</span>
          </p>
        ) : (
          <p className="text-xs text-[#b1543b] font-medium" data-testid="essentials-hint">
            Pick the Todoist list you want to see even on low-capacity days — your daily non-negotiables.
          </p>
        )}
      </div>

      {/* Supplements */}
      <div className="rounded-3xl bg-[#faf4ee] dark:bg-stone-900 border border-stone-100 dark:border-stone-800 p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-4" data-testid="supplements-editor">
        <div className="flex items-center gap-2">
          <Pill className="h-5 w-5 text-stone-400" />
          <h3 className="font-heading text-xl font-bold text-[#3b2a24] dark:text-stone-100">Supplements</h3>
        </div>
        <p className="text-xs text-stone-400">Leave every day unselected to take it daily, or tap days to schedule it just for those days.</p>
        <div className="space-y-3">
          {settings.supplements.map((s, idx) => {
            const everyday = !(s.days || []).length;
            return (
              <div key={s.name} className="rounded-2xl border border-stone-100 dark:border-stone-800 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-base font-medium text-[#3b2a24] dark:text-stone-100">{s.name}</span>
                  <button
                    data-testid={`remove-supplement-${s.name}`}
                    onClick={() => removeSupplement(idx)}
                    className="p-1.5 rounded-full text-stone-400 hover:text-rose-500 hover:bg-stone-100 dark:hover:bg-stone-800"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex gap-1.5">
                  {DAYS.map((d, di) => {
                    const on = (s.days || []).includes(di);
                    return (
                      <button
                        key={di}
                        data-testid={`supp-${s.name}-day-${di}`}
                        onClick={() => toggleSuppDay(idx, di)}
                        className={`h-8 w-8 rounded-full text-xs font-semibold transition-colors ${
                          on
                            ? "bg-[#b1543b] text-white"
                            : "bg-stone-100 dark:bg-stone-800 text-stone-400 dark:text-stone-500"
                        }`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-stone-400 mt-1.5">{everyday ? "Every day" : "Scheduled days only"}</p>
              </div>
            );
          })}
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="new-supplement-input"
            value={newSupp}
            onChange={(e) => setNewSupp(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addSupplement()}
            placeholder="Add a supplement…"
            className="h-11 rounded-2xl border-stone-200 dark:border-stone-700"
          />
          <Button
            data-testid="add-supplement-btn"
            onClick={addSupplement}
            className="h-11 rounded-2xl bg-[#b1543b] hover:bg-[#8f3f2b] text-white px-4"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-stone-400">Remember to tap "Save settings" below.</p>
      </div>

      {/* Integrations */}
      <div className="rounded-3xl bg-[#faf4ee] dark:bg-stone-900 border border-stone-100 dark:border-stone-800 p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="flex items-center gap-2 mb-4">
          <Plug className="h-5 w-5 text-stone-400" />
          <h3 className="font-heading text-xl font-bold text-[#3b2a24] dark:text-stone-100">Integrations</h3>
        </div>
        <div className="space-y-3">
          {[
            { name: "Todoist", on: integrations.todoist, hint: "Settings → Integrations → Developer" },
            { name: "Ultrahuman", on: integrations.ultrahuman, hint: "Ultrahuman Vision → Personal API Token" },
          ].map((it) => (
            <div key={it.name} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
              <div>
                <p className="text-base text-[#3b2a24] dark:text-stone-100">{it.name}</p>
                <p className="text-xs text-stone-400">{it.hint}</p>
              </div>
              <span
                data-testid={`integration-${it.name.toLowerCase()}`}
                className={`text-xs font-semibold px-3 py-1 rounded-full ${
                  it.on ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-400"
                }`}
              >
                {it.on ? "Connected" : "Not connected"}
              </span>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between py-3 mt-1 border-t border-stone-100 dark:border-stone-800">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-stone-400" />
            <div>
              <p className="text-base text-[#3b2a24] dark:text-stone-100">Google Calendar</p>
              <p className="text-xs text-stone-400">
                {cal.connected ? "Events power your schedule & clash warnings" : "See today's events & plan around meetings"}
              </p>
            </div>
          </div>
          {!cal.configured ? (
            <span className="text-xs font-semibold px-3 py-1 rounded-full bg-stone-100 text-stone-400">Unavailable</span>
          ) : cal.connected ? (
            <Button
              data-testid="disconnect-calendar-btn"
              variant="outline"
              onClick={disconnectCal}
              className="rounded-full h-9 text-sm border-stone-200 dark:border-stone-700"
            >
              Disconnect
            </Button>
          ) : (
            <Button
              data-testid="settings-connect-calendar-btn"
              onClick={connectCal}
              className="rounded-full h-9 text-sm bg-[#b1543b] hover:bg-[#8f3f2b] text-white"
            >
              Connect
            </Button>
          )}
        </div>
        {cal.connected && !cal.can_write && (
          <div
            data-testid="calendar-reconnect-note"
            className="mt-3 flex items-center justify-between gap-3 rounded-2xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 px-4 py-3"
          >
            <p className="text-xs text-amber-800 dark:text-amber-300">
              Reconnect to grant write access so Ebbly can add focus blocks to your calendar.
            </p>
            <Button
              data-testid="reconnect-calendar-btn"
              onClick={connectCal}
              className="rounded-full h-8 text-xs bg-[#b1543b] hover:bg-[#8f3f2b] text-white shrink-0"
            >
              Reconnect
            </Button>
          </div>
        )}
        <p className="text-xs text-stone-400 mt-4">
          API tokens are kept server-side in secrets, never in the app. Paste them into backend config to connect.
        </p>
      </div>

      <Button
        data-testid="save-settings"
        onClick={save}
        disabled={saving}
        className="w-full h-14 rounded-full bg-[#b1543b] hover:bg-[#8f3f2b] text-base font-semibold"
      >
        {saving ? "Saving…" : "Save settings"}
      </Button>
    </div>
  );
};
